import { create } from "zustand";

import type { ILayoutStoreBase, IMeta, IModal, ISubModal, IToast } from "@repo/shopifyV2/types/layout";
import type { Image, Maybe } from "@repo/shopifyV2/types/storefront.types";

import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from "@const/meta";

export interface IPopup {
  title: string;
  description: string;
  id?: string;
  handle?: string;
  image?: Maybe<Image>;
  url?: string;
  buttonText?: string;
  startDate: Date;
  endDate: Date;
}

export interface ILayoutStore extends ILayoutStoreBase {
  isOpenAccount: boolean;
  isOpenNavigation: boolean;
  isOpenMarket: boolean;
  isOpenMenu: boolean;

  toast: IToast[];
  subModal: ISubModal[];
  popup: IPopup | null;

  meta: IMeta;
  isLoading: boolean;

  closeToast: (toast: IToast[]) => void;
  openModal: (modal: IModal | null) => void;
  openCart: (isOpenCart: boolean) => void;
  openSearch: (isOpenSearch: boolean) => void;
  openAccount: (isOpenAccount: boolean) => void;
  openNavigation: (isOpenNavigation: boolean) => void;
  openMarket: (isOpenMarket: boolean) => void;
  openMenu: (isOpenMenu: boolean) => void;
  closeAll: () => void;

  openPopup: (modal: IPopup | null) => void;

  openSubModal: (subModal: ISubModal[]) => void;

  setIsLoading: (isLoading: boolean) => void;

  setMeta: (meta: IMeta) => void;
  clearMeta: () => void;
}

const initLayout = {
  isOpenAccount: false,
  isOpenCart: false,
  isOpenSearch: false,
  isOpenMarket: false,
  isOpenMenu: false,
  isOpenNavigation: false,

  isShowDim: false,
  isShowFooter: true,
  isLoading: false,

  toast: [{ message: "dummy", id: "-1" }], // 에니메이션 때문에 더미 데이터 넣기
  subModal: [],
  modal: null,
  popup: null,
  meta: { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
};

export const useLayoutStore = create<ILayoutStore>()((set) => ({
  // 초기값
  ...initLayout,

  // 함수
  showDim: (isShowDim: boolean) => {
    set(() => ({ isShowDim }));
  },
  openToast: (toast: IToast) => {
    const newToast = { ...toast, id: crypto.randomUUID() };
    set((state) => ({ toast: [...state.toast, newToast] }));

    // TODO 중간에 하나 닫으면 타이밍 느려짐 (setTimeout 클로저때문)
    setTimeout(() => {
      set((state) => ({ toast: [...state.toast.slice(0, 1), ...state.toast.slice(2)] }));
    }, 3000);
  },
  closeToast: (toast: IToast[]) => {
    set(() => ({ toast }));
  },
  openPopup: (popup: IPopup | null) => {
    set(() => ({ popup }));
  },
  openSubModal: (subModal: ISubModal[]) => {
    set(() => ({ subModal }));
  },
  closeSubModal: () => {
    set((state) => ({ subModal: [...state.subModal.slice(1)] }));
  },
  openModal: (modal: IModal | null) => {
    set(() => ({ modal }));
  },
  showFooter: (isShowFooter) => {
    set(() => ({ isShowFooter }));
  },
  openCart: (isOpenCart) => {
    set(() => ({ ...initLayout, isOpenMenu: isOpenCart, isOpenCart }));
  },
  openSearch: (isOpenSearch) => {
    set(() => ({ isOpenMenu: isOpenSearch, isOpenSearch }));
  },
  openAccount: (isOpenAccount) => {
    set(() => ({ isOpenMenu: isOpenAccount, isOpenAccount }));
  },
  openMarket: (isOpenMarket) => {
    set(() => ({ isOpenMenu: isOpenMarket, isOpenMarket }));
  },
  openNavigation: (isOpenNavigation) => {
    set(() => ({ isOpenMenu: isOpenNavigation, isOpenNavigation }));
  },
  openMenu: (isOpenMenu) => {
    set(() => ({ isOpenMenu }));
  },
  closeAll: () => {
    set((state) => ({
      ...initLayout,
      toast: state.toast,
      subModal: state.subModal,
      isShowFooter: state.isShowFooter,
      meta: state.meta,
    }));
  },
  setIsLoading: (isLoading: boolean) => {
    set(() => ({ isLoading }));
  },
  setMeta: (meta: IMeta) => {
    set(() => ({ meta }));
  },
  clearMeta: () => {
    set(() => ({ meta: initLayout.meta }));
  },
}));
